import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import PaymentScheduled from 'components/Core/Autopay-schedule/PaymentScheduled/PaymentScheduled';
import MainFooter from 'components/Core/Footer/MainFooterComponent/MainFooter';
import EstimateAccept from 'components/Estimate/Accept/EstimateAccept';
import EstimateDepositAccept from 'components/Estimate/Accept/EstimateDepositAccept';
import DeclineEstimateModal from 'components/Estimate/Declined/DeclineModal/DeclineEstimateModal';
import { renderStatusCard } from 'components/Estimate/StatusCard/statusCardRenderer';
import PaymentRequestMoreDetails from 'components/PaymentReq/MoreDetails/PaymentRequestMoreDetails';
import OneToManyMoreDetails from 'components/PaymentReq/OneToMany/MoreDetails/OneToManyMoreDetails';
import MobileInvoiceHeader from 'components/Shared/MobileInvoiceHeader/MobileInvoiceHeader';
import UnifiedPaymentComponent from 'components/Shared/UnifiedPaymentComponent/UnifiedPaymentComponent';
import { companyInfoSelectors } from 'store/companyInfo/selectors';
import { insightSelectors } from 'store/insight/selectors';
import { modalActions } from 'store/modal/slice';
import { saleSelectors } from 'store/sale/selectors';
import { setStatusAccept, setStatusDeclined } from 'store/sale/slice';
import { AppDispatch } from 'store/store';
import { CompanyInfo } from 'types/CompanyInfo';
import { Config } from 'types/Config';
import { FeatureFlags } from 'types/FeatureFlags';
import { IXP } from 'types/IXP';
import { Insight } from 'types/Insight';
import { Payment } from 'types/Payment';
import { Sale, SaleLine } from 'types/Sale';
import { PageVariant, SALE_MAP, CURRENCY } from 'types/constants';

// Reusable styles
const FEEDBACK_STYLES = `
  .feedback-w {
    @media not screen and (max-width: 768px) {
      display: none;
    }
  }
`;

interface ComponentRendererProps {
  pageVariant: PageVariant;
  sale: Sale | {};
  companyInfo: CompanyInfo | {};
  insight: Insight | {};
  ixp: IXP | {};
  auth: {
    isUserSignedIn?: boolean;
  };
  shouldShowChangeOrderFeatures: () => boolean;
  getDisplayAmount: () => number;
  getStatusCardTitle: () => React.ReactNode;
  pdfStatus: {
    downloading: boolean;
    error: boolean;
    url?: string;
  };
  config: Config;
  handleDownloadPDF: () => void;
  featureFlags?: FeatureFlags;
  payment?: Payment | {};
}

export const useComponentRenderer = (props: ComponentRendererProps) => {
  const {
    pageVariant,
    sale,
    ixp,
    companyInfo,
    auth,
    shouldShowChangeOrderFeatures,
    getDisplayAmount,
    getStatusCardTitle,
    pdfStatus,
    config,
    handleDownloadPDF,
    featureFlags,
    payment,
    insight,
  } = props;

  const dispatch = useDispatch<AppDispatch>();

  const declineModal = useCallback(() => {
    const ModalContent = () => (
      <DeclineEstimateModal
        hide={() => dispatch(modalActions.hide())}
        onCancel={() => dispatch(modalActions.hide())}
        onDecline={() => dispatch(setStatusDeclined())}
        isChangeOrder={shouldShowChangeOrderFeatures()}
      />
    );
    dispatch(
      modalActions.show({
        component: ModalContent,
        componentProps: {
          closeOnClickOutSide: true,
          closeOnEsc: true,
          showCloseButton: false,
        },
      })
    );
  }, [dispatch, shouldShowChangeOrderFeatures]);

  // Helper function to render one-to-many card
  const renderOneToManyCard = useCallback(() => {
    if (
      pageVariant !== PageVariant.PAYMENT_REQUEST ||
      !saleSelectors.isOneToManyPaymentRequest(sale as Sale)
    )
      return null;

    return (
      <div className="paylink-merchant-info-card">
        <OneToManyMoreDetails
          description={''} // Placeholder, as this component doesn't directly use these props
          estimatedDelivery={''} // Placeholder
          shouldBeInitiallyOpen={true}
        />
      </div>
    );
  }, [pageVariant, sale, dispatch]);

  return useCallback(() => {
    // Get estimate status for status card logic
    const estimateStatus = saleSelectors.estimateStatus(sale as Sale);

    // Handle payment request case
    if (pageVariant === PageVariant.PAYMENT_REQUEST) {
      const isOneToMany = saleSelectors.isOneToManyPaymentRequest(sale as Sale);
      const companyName = companyInfoSelectors.nameSelector(companyInfo as CompanyInfo) || '';
      const companyEmail = companyInfoSelectors.emailSelector(companyInfo as CompanyInfo) || '';
      const companyPhone = companyInfoSelectors.phoneSelector(companyInfo as CompanyInfo) || '';
      const companyWebAddr = companyInfoSelectors.webAddrSelector(companyInfo as CompanyInfo) || '';
      const companyAddress = companyInfoSelectors.addressSelector(companyInfo as CompanyInfo) || {};
      const number = saleSelectors.referenceNumberSelector(sale as Sale) || '';
      const lineItems = saleSelectors.lineItemsSelector(sale as Sale) || [];
      const description = saleSelectors.descriptionSelector(sale as Sale) || '';
      const txnDate = saleSelectors.txnDateSelector(sale as Sale) || '';
      const amount = saleSelectors.amountSelector(sale as Sale) || 0;
      const balanceAmount = saleSelectors.balanceSelector(sale as Sale) || 0;
      const currency = saleSelectors.currencySelector(sale as Sale) || CURRENCY.USD;
      const estimatedDelivery = saleSelectors.estimatedDeliverySelector(sale as Sale) || '';
      const companyLogoUrl = companyInfoSelectors.logoSelector(companyInfo as CompanyInfo) || '';

      const companyLocale = companyInfoSelectors.localeSelector(companyInfo as CompanyInfo) || '';
      const isPayable = insightSelectors.isPayableSelector(insight as Insight);

      return (
        <>
          <MobileInvoiceHeader companyLogoUrl={companyLogoUrl} companyName={companyName} />
          {isOneToMany ? (
            <OneToManyMoreDetails
              description={description}
              estimatedDelivery={estimatedDelivery}
              shouldBeInitiallyOpen={true}
            />
          ) : (
            <PaymentRequestMoreDetails
              showModal={(component, props) => dispatch(modalActions.show({ component, ...props }))}
              companyEmail={companyEmail}
              companyPhone={companyPhone}
              companyWebAddr={companyWebAddr}
              companyAddress={companyAddress}
              number={number}
              lineItems={lineItems as Array<SaleLine>}
              description={description}
              txnDate={txnDate}
              amount={amount}
              balanceAmount={balanceAmount}
              currency={currency}
              totalAmount={(payment as Payment)?.amount || 0}
            />
          )}

          <UnifiedPaymentComponent
            pageVariant={pageVariant}
            sale={sale as Sale}
            ixp={ixp as IXP}
            companyInfo={companyInfo as CompanyInfo}
            payment={payment as Payment}
            featureFlags={featureFlags || {}}
            onAccept={() => dispatch(setStatusAccept())}
            onDecline={declineModal}
            pdfStatus={pdfStatus}
            config={config}
          />
          <MainFooter companyLocale={companyLocale} isPayable={isPayable} />
        </>
      );
    }

    // Handle scheduled payment case
    if (pageVariant === PageVariant.SCHEDULED) {
      const isAutoPay = saleSelectors.isAutoPay(sale as Sale);
      const isSubscription = saleSelectors.isSubscription(sale as Sale);

      const recurringInfo = saleSelectors.getRecurringInfo(sale as Sale);
      const isUserSignedIn = auth.isUserSignedIn;
      const balanceAmount = saleSelectors.balanceSelector(sale as Sale) || 0;
      const scheduleDate = saleSelectors.getScheduleDate(sale as Sale);
      const canCancelSchedule = insightSelectors.canCancelSchedule(insight as Insight);
      // If page variant is SCHEDULED, always show PaymentScheduled component
      // Use current date as fallback if no schedule date is available
      const displayDate = scheduleDate || new Date();

      return (
        <PaymentScheduled
          balance={balanceAmount}
          currency={saleSelectors.currencySelector(sale as Sale) || CURRENCY.USD}
          companyName={companyInfoSelectors.nameSelector(companyInfo as CompanyInfo) || ''}
          isAutoPay={isAutoPay || false}
          isSubscription={isSubscription}
          scheduledDate={displayDate}
          isUserSignedIn={isUserSignedIn}
          isSignedInUserTheScheduler={canCancelSchedule}
          companyLocale={companyInfoSelectors.localeSelector(companyInfo as CompanyInfo) || ''}
          recurringInfo={recurringInfo}
          featureFlags={featureFlags || {}}
          isPaymentScheduleSuccessful={false}
        />
      );
    }

    // Handle status card variants (accepted, declined, expired)
    const statusCardVariants = [
      PageVariant.ESTIMATE_ACCEPTED,
      PageVariant.DEPOSIT_ACCEPTED,
      PageVariant.ESTIMATE_DECLINED,
      PageVariant.DEPOSIT_DECLINED,
      PageVariant.ESTIMATE_EXPIRED,
    ];

    if (statusCardVariants.includes(pageVariant) || estimateStatus === SALE_MAP.STATUS.REJECTED) {
      return renderStatusCard({
        insightData: { pageVariant },
        shouldShowChangeOrderFeatures,
        getDisplayAmount,
        getStatusCardTitle,
        sale,
        companyInfo,
        pdfStatus,
        config,
        handleDownloadPDF,
        estimateStatus,
      });
    }

    // Handle subscription and deposit pending pages
    if (pageVariant === PageVariant.SUBSCRIPTION || pageVariant === PageVariant.DEPOSIT_PENDING) {
      return (
        <UnifiedPaymentComponent
          pageVariant={pageVariant}
          sale={sale as Sale}
          companyInfo={companyInfo as CompanyInfo}
          payment={payment as Payment}
          featureFlags={featureFlags || {}}
          ixp={ixp as IXP}
          config={config}
          onAccept={
            pageVariant === PageVariant.DEPOSIT_PENDING
              ? () => dispatch(setStatusAccept())
              : () => {} // Subscriptions don't have accept actions
          }
          onDecline={
            pageVariant === PageVariant.DEPOSIT_PENDING ? declineModal : () => {} // Subscriptions don't have decline actions
          }
          pdfStatus={pdfStatus}
        />
      );
    }

    if (pageVariant === PageVariant.ESTIMATE_PENDING) {
      const depositAmount = saleSelectors.depositAmountSelector(sale as Sale) || 0;
      if (depositAmount > 0) {
        return (
          <>
            <EstimateDepositAccept
              companyName={companyInfoSelectors.nameSelector(companyInfo as CompanyInfo) || ''}
              companyEmail={companyInfoSelectors.emailSelector(companyInfo as CompanyInfo) || ''}
              amount={saleSelectors.amountSelector(sale as Sale) || 0}
              depositAmount={depositAmount}
              currency={saleSelectors.currencySelector(sale as Sale) || CURRENCY.USD}
              locale={companyInfoSelectors.localeSelector(companyInfo as CompanyInfo) || ''}
              pdfUrl={pdfStatus.url}
              isPDFError={pdfStatus.error}
              acceptHandler={() => dispatch(setStatusAccept())}
              viewEstimateHandler={() => handleDownloadPDF()}
              declineHandler={declineModal}
            />
            <style jsx>{FEEDBACK_STYLES}</style>
          </>
        );
      }
      return (
        <>
          <EstimateAccept
            companyName={companyInfoSelectors.nameSelector(companyInfo as CompanyInfo) || ''}
            amount={saleSelectors.amountSelector(sale as Sale) || 0}
            currency={saleSelectors.currencySelector(sale as Sale) || CURRENCY.USD}
            isPDFError={pdfStatus.error}
            locale={companyInfoSelectors.localeSelector(companyInfo as CompanyInfo) || ''}
            pdfUrl={pdfStatus.url || undefined}
            acceptHandler={() => dispatch(setStatusAccept())}
            viewEstimateHandler={() => handleDownloadPDF()}
            isChangeOrder={shouldShowChangeOrderFeatures()}
            declineHandler={declineModal}
          />
          <style jsx>{FEEDBACK_STYLES}</style>
        </>
      );
    }

    // Default case - return null if no conditions match
    return null;
  }, [
    pageVariant,
    sale,
    companyInfo,
    shouldShowChangeOrderFeatures,
    getDisplayAmount,
    getStatusCardTitle,
    pdfStatus,
    config,
    handleDownloadPDF,
    featureFlags,
    payment,
    auth,
    dispatch,
    declineModal,
    renderOneToManyCard,
  ]);
};
